import { useCallback, useRef, type ReactNode } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
	courseRowActionColor,
	formControlHeight,
	formPrimary,
	getSelectChevron,
	selectChevronCapClassName,
	selectChevronCapDisabledClassName,
	selectChevronDisabledIconColor,
} from "@config/theme/formControls";
import {
	PORTAL_FORM_FILTER_SELECT_CHEVRON,
	PORTAL_FORM_FILTER_SELECT_CONTROL_HEIGHT,
} from "@constants/portalUiTheme";

interface SelectFieldWrapperProps {
	children: ReactNode;
	className?: string;
	controlHeight?: number;
	disabled?: boolean;
}

export const SelectFieldWrapper = ({
	children,
	className = "",
	controlHeight = formControlHeight,
	disabled = false,
}: SelectFieldWrapperProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const isJobsFilterSelect = controlHeight === PORTAL_FORM_FILTER_SELECT_CONTROL_HEIGHT;
	const chevron = getSelectChevron(controlHeight);
	const isCourseRowTile = controlHeight === 44;
	const filterChevron = PORTAL_FORM_FILTER_SELECT_CHEVRON;
	const capHeight = isJobsFilterSelect
		? filterChevron.size
		: controlHeight <= 32
			? Math.min(chevron.height, controlHeight - 6)
			: chevron.height;
	const capWidth = isJobsFilterSelect
		? filterChevron.size
		: controlHeight <= 32
			? Math.min(chevron.width, capHeight + 2)
			: chevron.width;
	const iconSize = isCourseRowTile
		? 18
		: isJobsFilterSelect
			? filterChevron.icon
			: controlHeight <= 32
				? 10
				: controlHeight <= 35
					? 12
					: controlHeight <= 36
						? 13
						: controlHeight < 48
							? 14
							: 16;
	const capRight = isJobsFilterSelect ? filterChevron.rightInset : chevron.rightInset;
	const capRadius = isJobsFilterSelect ? filterChevron.borderRadius : chevron.borderRadius;

	const openSelect = useCallback(() => {
		if (disabled) return;

		const root = wrapperRef.current;
		if (!root) return;

		const combo = root.querySelector('[role="combobox"]') as HTMLElement | null;
		const selectArea = root.querySelector(".MuiSelect-select") as HTMLElement | null;
		const target = combo ?? selectArea;
		if (!target) return;

		target.dispatchEvent(
			new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window }),
		);
		target.click();
	}, [disabled]);

	const actionBackgroundColor = disabled
		? undefined
		: isCourseRowTile
			? courseRowActionColor
			: formPrimary;

	return (
		<div
			ref={wrapperRef}
			className={`relative flex w-full items-stretch [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!max-h-none [&_.MuiInputBase-root]:!shadow-none ${className}`}
			style={isJobsFilterSelect || controlHeight <= 36 ? { maxHeight: controlHeight } : undefined}>
			{children}
			<button
				type="button"
				className={disabled ? selectChevronCapDisabledClassName : selectChevronCapClassName}
				style={{
					top: "50%",
					transform: "translateY(-50%)",
					width: capWidth,
					height: capHeight,
					borderRadius: capRadius,
					right: capRight,
					backgroundColor: actionBackgroundColor,
					pointerEvents: disabled ? "none" : "auto",
				}}
				onClick={openSelect}
				onMouseDown={e => e.preventDefault()}
				disabled={disabled}
				aria-label="Open dropdown"
				aria-disabled={disabled || undefined}
				tabIndex={-1}
			>
				<KeyboardArrowDownIcon
					sx={{
						color: disabled ? selectChevronDisabledIconColor : "#fff",
						fontSize: iconSize,
						pointerEvents: "none",
					}}
				/>
			</button>
		</div>
	);
};
